/**
 * Elevation Data Storage Utility
 *
 * Stores elevation with a plan id so cache from one plan is never shown for another.
 */

const ELEVATION_STORAGE_KEY = 'ultrapackr_elevation_data';
const ELEVATION_TIMESTAMP_KEY = 'ultrapackr_elevation_timestamp';

/**
 * Normalize payload from storage (supports legacy array-only JSON).
 * @param {unknown} parsed
 * @returns {{ data: Array, planId: string|null } | null}
 */
function normalizeStoredRecord(parsed) {
  if (Array.isArray(parsed)) {
    return { data: parsed, planId: null };
  }
  if (parsed && Array.isArray(parsed.data)) {
    const pid = parsed.planId;
    return {
      data: parsed.data,
      planId: pid !== undefined && pid !== null && pid !== '' ? String(pid) : null
    };
  }
  return null;
}

/**
 * Save elevation data to both sessionStorage and localStorage
 * @param {Array} elevationData - The elevation data array
 * @param {string|null|undefined} planId - Plan id (required for reliable reload; may be null during GPX upload before id exists)
 * @returns {boolean} - Whether the save was successful
 */
export const saveElevationData = (elevationData, planId = null) => {
  if (!elevationData || !Array.isArray(elevationData) || elevationData.length === 0) {
    console.warn('saveElevationData: No valid elevation data to save');
    return false;
  }

  try {
    const dataToStore = {
      data: elevationData,
      timestamp: Date.now(),
      planId: planId !== undefined && planId !== null && planId !== '' ? String(planId) : null,
      length: elevationData.length
    };

    const jsonString = JSON.stringify(dataToStore);

    try {
      sessionStorage.setItem(ELEVATION_STORAGE_KEY, jsonString);
      console.log(
        `Saved ${elevationData.length} elevation points to sessionStorage (planId=${dataToStore.planId || 'none'})`
      );
    } catch (sessionError) {
      console.warn('sessionStorage save failed:', sessionError);
    }

    try {
      localStorage.setItem(ELEVATION_STORAGE_KEY, jsonString);
      localStorage.setItem(ELEVATION_TIMESTAMP_KEY, Date.now().toString());
      console.log(`Saved ${elevationData.length} elevation points to localStorage (backup)`);
    } catch (localError) {
      console.warn('localStorage save failed:', localError);
    }

    return true;
  } catch (error) {
    console.error('Error saving elevation data:', error);
    return false;
  }
};

/**
 * Load elevation data only if it was saved for this plan id.
 * @param {string|null|undefined} planId - Current race plan id (falsy => no cache read — avoids cross-plan bleed)
 * @returns {Array|null}
 */
export const loadElevationData = (planId) => {
  if (planId === undefined || planId === null || planId === '') {
    console.log('loadElevationData: skipped — no plan id (refusing unscoped cache)');
    return null;
  }
  const expected = String(planId);

  try {
    let dataString = sessionStorage.getItem(ELEVATION_STORAGE_KEY);

    if (dataString) {
      const parsed = JSON.parse(dataString);
      const record = normalizeStoredRecord(parsed);
      if (record && record.planId === expected) {
        console.log(`Loaded ${record.data.length} elevation points from sessionStorage (plan ${expected})`);
        return record.data;
      }
      if (record) {
        console.log(
          `Ignoring sessionStorage elevation (plan mismatch: cache=${record.planId}, want=${expected})`
        );
      }
    }

    dataString = localStorage.getItem(ELEVATION_STORAGE_KEY);

    if (dataString) {
      const parsed = JSON.parse(dataString);
      const record = normalizeStoredRecord(parsed);
      if (record && record.planId === expected) {
        console.log(`Loaded ${record.data.length} elevation points from localStorage (plan ${expected})`);
        try {
          sessionStorage.setItem(ELEVATION_STORAGE_KEY, dataString);
        } catch (sessionError) {
          console.warn('Could not restore to sessionStorage:', sessionError);
        }
        return record.data;
      }
      if (record) {
        console.log(
          `Ignoring localStorage elevation (plan mismatch: cache=${record.planId}, want=${expected})`
        );
      }
    }

    console.log('No elevation data in storage for this plan');
    return null;
  } catch (error) {
    console.error('Error loading elevation data:', error);
    return null;
  }
};

/**
 * Clear elevation data from both storages
 */
export const clearElevationData = () => {
  try {
    sessionStorage.removeItem(ELEVATION_STORAGE_KEY);
    localStorage.removeItem(ELEVATION_STORAGE_KEY);
    localStorage.removeItem(ELEVATION_TIMESTAMP_KEY);
    console.log('Cleared elevation data from storage');
  } catch (error) {
    console.error('Error clearing elevation data:', error);
  }
};

/**
 * Get the timestamp of the last elevation data save
 * @returns {number|null} - Timestamp in milliseconds or null
 */
export const getElevationDataTimestamp = () => {
  try {
    const timestamp = localStorage.getItem(ELEVATION_TIMESTAMP_KEY);
    return timestamp ? parseInt(timestamp, 10) : null;
  } catch (error) {
    console.error('Error getting elevation data timestamp:', error);
    return null;
  }
};

/**
 * Check if elevation data exists in storage (any plan — used for diagnostics only)
 * @returns {boolean}
 */
export const hasElevationData = () => {
  try {
    return !!(sessionStorage.getItem(ELEVATION_STORAGE_KEY) || localStorage.getItem(ELEVATION_STORAGE_KEY));
  } catch (error) {
    return false;
  }
};

const elevationStorage = {
  saveElevationData,
  loadElevationData,
  clearElevationData,
  getElevationDataTimestamp,
  hasElevationData
};

export default elevationStorage;
